import { useId, useRef, useState } from "react";
import { ImagePlus, QrCode, Trash2, Type } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  MAX_BACKGROUND_BYTES,
  SEAL_PLACEHOLDERS,
  fileToBase64,
  isOffLabel,
  newQrElement,
  newTextElement,
  sealCanvas,
  type ColorMode,
  type SealElement,
  type SealTemplate,
} from "@/utils/boxTopper";
import { LOGO_FILE_TYPES, logoFileToBackground } from "@/utils/boxTopperLogo";

interface SealTemplateFormProps {
  template: SealTemplate;
  colorMode: ColorMode;
  onChange: (template: SealTemplate) => void;
}

function NumberField({
  label,
  value,
  onChange,
  min,
  max,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
  min?: number;
  max?: number;
}) {
  const id = useId();
  return (
    <div className="min-w-0 space-y-1">
      <Label htmlFor={id} className="text-xs text-muted-foreground">
        {label}
      </Label>
      <Input
        id={id}
        type="number"
        inputMode="numeric"
        className="h-11 tabular-nums sm:h-10"
        value={Number.isFinite(value) ? value : ""}
        min={min}
        max={max}
        onChange={(e) => onChange(e.target.value === "" ? Number.NaN : Number(e.target.value))}
      />
    </div>
  );
}

/**
 * The seal template as a form (no drag-and-drop): an optional logo (composed
 * into the clear top of a vertical strip) or full-strip artwork; an older
 * landscape label keeps its whole-label background image; then one row per
 * text or QR element with its position, size, font size and ink. All numbers
 * are printer dots on the template's own canvas (portrait 696 wide x 1800
 * tall, or an older landscape 1800 x 696; 300 dpi), the relay's own units, so
 * what is typed here is exactly what prints.
 */
export function SealTemplateForm({ template, colorMode, onChange }: SealTemplateFormProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const artworkRef = useRef<HTMLInputElement>(null);
  const [logoSmall, setLogoSmall] = useState(false);
  const [backgroundError, setBackgroundError] = useState<string | null>(null);
  const canvas = sealCanvas(template);
  const rotated = template.label?.rotate_180 === true;

  const setElement = (index: number, next: SealElement) =>
    onChange({ ...template, elements: template.elements.map((el, i) => (i === index ? next : el)) });
  const removeElement = (index: number) =>
    onChange({ ...template, elements: template.elements.filter((_, i) => i !== index) });

  const onBackground = async (file: File | undefined) => {
    setBackgroundError(null);
    if (!file) return;
    if (file.type !== "image/png") {
      setBackgroundError("Use a PNG image.");
      return;
    }
    if (file.size > MAX_BACKGROUND_BYTES) {
      setBackgroundError(`That image is over 1 MB. Export a smaller PNG, ideally ${canvas.width} x ${canvas.height} pixels.`);
      return;
    }
    onChange({ ...template, background_png_base64: await fileToBase64(file) });
    setLogoSmall(false);
  };

  // A vertical strip takes a logo, composed into the clear box at the top.
  const onLogo = async (file: File | undefined) => {
    setBackgroundError(null);
    if (!file) return;
    try {
      const { png, small } = await logoFileToBackground(file, canvas);
      onChange({ ...template, background_png_base64: png });
      setLogoSmall(small);
    } catch (e) {
      setBackgroundError(e instanceof Error ? e.message : "That logo could not be used.");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <Label id="bt-rotate-label" htmlFor="bt-rotate" className="text-sm font-semibold text-foreground">
            Flip the label end for end
          </Label>
          <p id="bt-rotate-desc" className="mt-1 text-xs leading-relaxed text-muted-foreground">
            Turns the whole seal upside down when it prints. Leave it off for the first real seal; if the top of the
            design comes out at the wrong end of the box, turn it on. The previews always show the design upright.
          </p>
        </div>
        <Switch
          id="bt-rotate"
          aria-labelledby="bt-rotate-label"
          aria-describedby="bt-rotate-desc"
          checked={rotated}
          onCheckedChange={(on) => onChange({ ...template, label: { ...(template.label ?? {}), rotate_180: on } })}
        />
      </div>

      {canvas.orientation === "portrait" ? (
        <div className="space-y-2">
          <p id="bt-logo-label" className="text-sm font-semibold text-foreground">
            Logo (shown centered at the top)
          </p>
          <p id="bt-logo-desc" className="text-xs text-muted-foreground">
            Optional. Your logo is fitted into the space above "ONLY AT THIS LOCATION" and centered there. The roll
            prints black only, so the logo prints in black and grey tones; a simple dark logo on a light or clear
            background works best. PNG, JPEG or WebP, up to 5 MB.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept={LOGO_FILE_TYPES.join(",")}
              className="sr-only"
              aria-label="Upload logo"
              onChange={(e) => {
                void onLogo(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
            <Button
              type="button"
              variant="outline"
              className="h-11 sm:h-10"
              aria-describedby="bt-logo-desc"
              onClick={() => fileRef.current?.click()}
            >
              <ImagePlus className="mr-2 h-4 w-4" aria-hidden />
              {template.background_png_base64 ? "Replace logo" : "Upload logo"}
            </Button>
            {template.background_png_base64 && (
              <Button
                type="button"
                variant="ghost"
                className="h-11 text-muted-foreground sm:h-10"
                onClick={() => {
                  setBackgroundError(null);
                  setLogoSmall(false);
                  onChange({ ...template, background_png_base64: null });
                }}
              >
                Remove logo
              </Button>
            )}
          </div>
          {logoSmall && template.background_png_base64 && (
            <p className="text-xs text-muted-foreground">
              This logo is small and will print small; a larger image prints sharper.
            </p>
          )}

          <div className="space-y-2 border-t border-border pt-4">
            <p className="text-sm font-semibold text-foreground">Full-strip artwork (replaces the logo)</p>
            <p id="bt-artwork-desc" className="text-xs text-muted-foreground">
              Optional. A PNG up to 1 MB, best at {canvas.width} x {canvas.height} pixels, stretched to the whole label
              and printed behind everything. The logo and the artwork are stored in the same background field, so the
              last upload wins. The roll prints black only.
            </p>
            <input
              ref={artworkRef}
              type="file"
              accept="image/png"
              className="sr-only"
              aria-label="Upload full-strip artwork"
              onChange={(e) => {
                void onBackground(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
            <Button
              type="button"
              variant="outline"
              className="h-11 sm:h-10"
              aria-describedby="bt-artwork-desc"
              onClick={() => artworkRef.current?.click()}
            >
              <ImagePlus className="mr-2 h-4 w-4" aria-hidden />
              Upload artwork
            </Button>
          </div>
          {backgroundError && (
            <p role="alert" className="text-xs text-status-danger">
              {backgroundError}
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-sm font-semibold text-foreground">Background image</p>
          <p className="text-xs text-muted-foreground">
            Optional artwork printed behind everything, stretched to the whole label. Best at {canvas.width} x {canvas.height}{" "}
            pixels. Pure red areas print red on a black and red roll; everything else prints black.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/png"
              className="sr-only"
              aria-label="Upload background image"
              onChange={(e) => {
                void onBackground(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
            <Button type="button" variant="outline" className="h-11 sm:h-10" onClick={() => fileRef.current?.click()}>
              <ImagePlus className="mr-2 h-4 w-4" aria-hidden />
              {template.background_png_base64 ? "Replace image" : "Upload image"}
            </Button>
            {template.background_png_base64 && (
              <Button
                type="button"
                variant="ghost"
                className="h-11 text-muted-foreground sm:h-10"
                onClick={() => onChange({ ...template, background_png_base64: null })}
              >
                Remove image
              </Button>
            )}
          </div>
          {backgroundError && <p className="text-xs text-status-danger">{backgroundError}</p>}
        </div>
      )}

      <div className="space-y-3">
        <div>
          <p className="text-sm font-semibold text-foreground">Elements</p>
          <p className="text-xs text-muted-foreground">
            Positions and sizes are printer dots: the label is {canvas.width} wide by {canvas.height} tall, 300 dots to
            the inch. Text can use {SEAL_PLACEHOLDERS.map((p) => `{${p}}`).join(" ")}.
          </p>
        </div>

        <ol className="divide-y divide-border border-y border-border">
          {template.elements.map((el, index) => (
            <li key={index} className="py-4" aria-label={`Element ${index + 1}`}>
              <div className="mb-3 flex items-center justify-between gap-2">
                <p className="flex min-w-0 items-center gap-2 text-sm font-medium text-foreground">
                  {el.type === "text" ? <Type className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden /> : <QrCode className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />}
                  <span className="truncate">
                    {index + 1}. {el.type === "text" ? "Text" : "QR code"}
                  </span>
                </p>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-11 text-muted-foreground sm:h-9"
                  aria-label={`Remove element ${index + 1}`}
                  onClick={() => removeElement(index)}
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </Button>
              </div>

              {el.type === "text" ? (
                <div className="space-y-1">
                  <Label htmlFor={`el-${index}-text`} className="text-xs text-muted-foreground">
                    Text
                  </Label>
                  <Input
                    id={`el-${index}-text`}
                    className="h-11 text-base sm:h-10 sm:text-sm"
                    value={el.text}
                    onChange={(e) => setElement(index, { ...el, text: e.target.value })}
                  />
                </div>
              ) : (
                <div className="space-y-1">
                  <Label htmlFor={`el-${index}-data`} className="text-xs text-muted-foreground">
                    QR encodes
                  </Label>
                  <Input
                    id={`el-${index}-data`}
                    className="h-11 text-base sm:h-10 sm:text-sm"
                    value={el.data}
                    onChange={(e) => setElement(index, { ...el, data: e.target.value })}
                    aria-describedby={`el-${index}-data-help`}
                  />
                  <div id={`el-${index}-data-help`} className="space-y-1 text-xs text-muted-foreground">
                    <p>
                      Use {"{voucher_url}"} for a unique voucher QR on each seal, or type a fixed link such as
                      sms:+1XXXXXXXXXX?&amp;body=JOIN to open a pre-filled text message, or any web address.
                    </p>
                    <p>
                      When no voucher placeholder is used, no Kudos voucher is created and Kudos settings are not needed.
                    </p>
                  </div>
                </div>
              )}

              <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <NumberField label="Left (x)" value={el.x} onChange={(x) => setElement(index, { ...el, x })} />
                <NumberField label="Top (y)" value={el.y} onChange={(y) => setElement(index, { ...el, y })} />
                {el.type === "text" ? (
                  <>
                    <NumberField label="Width" value={el.w} min={1} onChange={(w) => setElement(index, { ...el, w })} />
                    <NumberField label="Height" value={el.h} min={1} onChange={(h) => setElement(index, { ...el, h })} />
                    <NumberField
                      label="Font size"
                      value={el.font_size}
                      min={4}
                      max={2000}
                      onChange={(font_size) => setElement(index, { ...el, font_size })}
                    />
                  </>
                ) : (
                  <NumberField label="Size" value={el.size} min={1} onChange={(size) => setElement(index, { ...el, size })} />
                )}

                {el.type === "text" && (
                  <div className="min-w-0 space-y-1">
                    <Label className="text-xs text-muted-foreground" id={`el-${index}-font`}>
                      Weight
                    </Label>
                    <Select value={el.font ?? "regular"} onValueChange={(font) => setElement(index, { ...el, font: font as "regular" | "bold" })}>
                      <SelectTrigger className="h-11 sm:h-10" aria-labelledby={`el-${index}-font`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="regular">Regular</SelectItem>
                        <SelectItem value="bold">Bold</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {el.type === "text" && (
                  <div className="min-w-0 space-y-1">
                    <Label className="text-xs text-muted-foreground" id={`el-${index}-align`}>
                      Align
                    </Label>
                    <Select
                      value={el.align ?? "left"}
                      onValueChange={(align) => setElement(index, { ...el, align: align as "left" | "center" | "right" })}
                    >
                      <SelectTrigger className="h-11 sm:h-10" aria-labelledby={`el-${index}-align`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="left">Left</SelectItem>
                        <SelectItem value="center">Center</SelectItem>
                        <SelectItem value="right">Right</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="min-w-0 space-y-1">
                  <Label className="text-xs text-muted-foreground" id={`el-${index}-color`}>
                    Ink
                  </Label>
                  <Select
                    value={el.color ?? "black"}
                    onValueChange={(color) => setElement(index, { ...el, color: color as "black" | "red" })}
                  >
                    <SelectTrigger className="h-11 sm:h-10" aria-labelledby={`el-${index}-color`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="black">Black</SelectItem>
                      <SelectItem value="red">Red</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {el.color === "red" && colorMode === "black" && (
                <p className="mt-2 text-xs text-muted-foreground">Prints black while the label is set to black only.</p>
              )}
              {isOffLabel(el, canvas) && (
                <p className="mt-2 text-xs text-status-warning">Part of this element is off the label and will be cut off.</p>
              )}
            </li>
          ))}
        </ol>

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            className="h-11 sm:h-10"
            onClick={() => onChange({ ...template, elements: [...template.elements, newTextElement(canvas)] })}
          >
            <Type className="mr-2 h-4 w-4" aria-hidden />
            Add text
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-11 sm:h-10"
            onClick={() => onChange({ ...template, elements: [...template.elements, newQrElement(canvas)] })}
          >
            <QrCode className="mr-2 h-4 w-4" aria-hidden />
            Add QR code
          </Button>
        </div>
      </div>
    </div>
  );
}
